import { useState, useEffect, useRef, useCallback} from 'react';
import { useNavigate, useLocation} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useAlarms, useTenant} from '@tyndo/hooks';
import {
 Button,
 DropdownMenu,
 DropdownMenuTrigger,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuLabel,
 DropdownMenuSeparator,
 DropdownMenuSub,
 DropdownMenuSubTrigger,
 DropdownMenuSubContent,
 DropdownMenuRadioGroup,
 DropdownMenuRadioItem,
} from '@tyndo/ui-kit';
import { useAuthRoles } from '../auth/useSyncAuthRoles';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import NavModeSwitcher from './NavModeSwitcher';
import AdaptiveControl from '../adaptive/AdaptiveControl';
import type { AdaptiveShell } from '../adaptive/useAdaptiveShell';
import { viewRegistry, moduleManifestRegistry } from '../registries';
import { useIsModulePermitted } from './ModuleAccessContext';
import { friendlyModuleLabel, resolveSlugToViewId } from '../utils/urlHelpers';
import {
 useUiPreferences,
 toggleTheme,
 setDensity,
 DENSITY_OPTIONS,
 type DensityPreference,
} from './uiPreferences';
import {
 Bell,
 LogOut,
 Settings,
 User,
 Sun,
 Moon,
 Search,
 Sparkles,
 Menu,
 X,
 LayoutGrid,
 MoreVertical,
 Building2,
 Gauge,
 Check,
} from 'lucide-react';

interface TopBarProps {
 onOpenPalette: () => void;
 /** Toggle the mobile slide-over sidebar; supplied by AppShell. */
 onToggleMobileNav?: () => void;
 /** Whether the mobile slide-over is currently open. */
 mobileNavOpen?: boolean;
 /** Adaptive-UI facade — drives the visible personalization control. */
 adaptive?: AdaptiveShell;
}

export default function TopBar({ onOpenPalette, onToggleMobileNav, mobileNavOpen, adaptive }: TopBarProps) {
 const navigate = useNavigate();
 const location = useLocation();
 const { user, logout} = useAuth();
 const { tenant, setTenant, tenants } = useTenant();
 // Widget administration is an admin-only surface; mirror the role check the
 // landing-page editor uses for its admin-lock controls. Roles come from the
 // decoded JWT (useCurrentUser), not the name/email-only useAuth().user.
 const userRoles: string[] = useAuthRoles();
 const isAdmin =
 userRoles.includes('Administrator') ||
 userRoles.includes('admin') ||
 userRoles.some((r) => typeof r === 'string' && r.startsWith('widgets.'));
 const { t } = useTranslation();
 const alarms = useAlarms();
 const alarmCount = alarms?.length ?? 0;
 const [userMenuOpen, setUserMenuOpen] = useState(false);
 // Theme + density come from the shared shell preference store: persisted to
 // localStorage and synced across tabs (BroadcastChannel / storage events).
 const { theme, density } = useUiPreferences();
 const darkMode = theme === 'dark';

 const userMenuRef = useRef<HTMLDivElement>(null);

 // The single way this bar asks for the Copilot, shared by the visible trigger
 // and the kebab item so the two cannot drift apart. AppShell owns the panel's
 // open state and listens for the shortcut, so re-broadcasting it is how a
 // click reaches that state without threading a callback through the bar.
 const openCopilot = useCallback(() => {
 window.dispatchEvent(
 new KeyboardEvent('keydown', { key: 'j', metaKey: true, ctrlKey: true }),
 );
 }, []);

 useEffect(() => {
 const handleClickOutside = (e: MouseEvent) => {
 if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
 setUserMenuOpen(false);
}
};
 document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

 const densityLabel: Record<DensityPreference, string> = {
 comfortable: t('topbar.densityComfortable'),
 compact: t('topbar.densityCompact'),
 touch: t('topbar.densityTouch'),
 wall: t('topbar.densityWall'),
};

 // Current-location readout — a single, static "where am I" label (window-
 // title style), resolved from the view registry. The breadcrumb TRAIL is
 // owned by the PageHeader design-system primitive inside each view
 // (docs/ui-architecture.md); the TopBar deliberately renders no second,
 // competing trail. URL shapes handled:
 //   /{moduleId}                          — module landing
 //   /{moduleId}/{viewSlug}               — clean URL
 //   /{moduleId}/view/{viewId}            — legacy URL
 //   /{moduleId}/{viewSlug}/{entityId}    — drill-through
 const rawSegments = location.pathname.split('/').filter(Boolean);
 // The legacy form is `/<moduleId>/view/<viewId>`; drop the literal
 // "view" hop so the label reads "Module · View label".
 const pathSegments = rawSegments.filter((s) => s !== 'view');
 const moduleSeg = pathSegments[0];
 // The readout is DERIVED FROM THE URL, so it must pass the same entitlement
 // gate every other surface does. The manifest is public and carries display
 // names for modules the principal may not open; without this check, typing
 // the URL of a denied module printed its name in the header — disclosing
 // exactly what ModuleView's refusal state deliberately withholds (it renders
 // only the identifier the operator already typed). An unresolved or failed
 // gate withholds it too: nothing is permitted until the answer is in.
 const moduleSegPermitted = useIsModulePermitted(moduleSeg);
 const moduleEntry =
 moduleSeg && moduleSegPermitted ? moduleManifestRegistry.get(moduleSeg) : undefined;
 const moduleLabel =
 moduleSeg && moduleSegPermitted
 ? friendlyModuleLabel(moduleSeg, moduleEntry?.name as string | undefined)
 : '';
 const allViews = viewRegistry.listViews();
 const viewSeg = pathSegments[1];
 const fullViewId =
 viewSeg && moduleSegPermitted
 ? resolveSlugToViewId(
 viewSeg,
 allViews.filter((v) => v.moduleId === moduleSeg).map((v) => v.viewId),
 )
 : '';
 const viewLabel = fullViewId
 ? allViews.find((v) => v.viewId === fullViewId)?.viewName
 : undefined;
 const currentLocationLabel = [moduleLabel, viewLabel]
 .filter(Boolean)
 .join(' · ');

 const handleLogout = () => {
 logout();
 navigate('/login');
};

 // Appearance controls shared by the user menu (the Settings menu) — theme +
 // density both persist and sync across tabs through the preference store.
 const appearanceSection = (
 <div className="px-3 py-2 border-b border-border">
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
 {t('topbar.appearance')}
 </p>
 <Button
 type="button"
 variant="ghost"
 onClick={toggleTheme}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal flex items-center gap-2.5 rounded-md px-1.5 py-1.5 text-sm text-foreground hover:bg-accent transition-colors"
 aria-label={darkMode ? t('a11y.switchToLightMode') : t('a11y.switchToDarkMode')}
 >
 {darkMode ? (
 <Sun className="w-4 h-4 text-alarm-minor" />
 ) : (
 <Moon className="w-4 h-4 text-muted-foreground" />
 )}
 {darkMode ? t('topbar.lightMode') : t('topbar.darkMode')}
 </Button>
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mt-2 mb-1">
 {t('topbar.density')}
 </p>
 <div role="radiogroup" aria-label={t('topbar.density')} className="flex flex-col">
 {DENSITY_OPTIONS.map((d) => (
 <Button
 key={d}
 type="button"
 variant="ghost"
 role="radio"
 aria-checked={density === d}
 aria-label={t('a11y.setDensity', { density: densityLabel[d] })}
 onClick={() => setDensity(d)}
 className={`h-auto whitespace-normal w-full justify-start text-left font-normal flex items-center gap-2.5 rounded-md px-1.5 py-1.5 text-sm transition-colors ${
 density === d ? 'text-primary font-medium' : 'text-foreground hover:bg-accent'
}`}
 >
 <Gauge className="w-4 h-4 text-muted-foreground" />
 <span className="flex-1 text-left">{densityLabel[d]}</span>
 {density === d && <Check className="w-3.5 h-3.5 text-primary" />}
 </Button>
 ))}
 </div>
 </div>
 );

 return (
 <header className="glass-strong sticky top-0 z-30 border-b border-border/60 pt-[env(safe-area-inset-top)]">
 <div className="flex items-center justify-between h-14 gap-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
 {/* Left: Workspace + current-location label (sidebar collapse lives in
 the sidebar footer). flex-[2] (vs the palette's flex-1) so the left
 cluster's fixed-width chrome (workspace / nav-mode / adaptive
 controls) gets enough share to fit at laptop widths — with equal
 shares it overflowed and painted UNDER the palette box, making the
 adaptive control unclickable. The location label is the shrink
 absorber (min-w-0 + truncate). */}
 {/* `overflow-hidden` is the backstop, not the mechanism. The breakpoints
 above are what keep this cluster inside its share; this makes the
 failure mode a clipped control rather than one PAINTED OVER the
 search field, which is what an operator actually met at 1280px —
 the overlapping control is unclickable and there is no visual cue
 that anything is wrong. */}
 <div className="flex items-center gap-2 flex-[2] min-w-0 overflow-hidden">
 {/* Hamburger — visible only below md. Toggles the AppShell's slide-over
 navigation. Keyboard-focusable; aria-expanded reflects current state;
 the icon cross-fades/rotates between menu and close. */}
 {onToggleMobileNav && (
 <Button
 type="button"
 variant="ghost"
 onClick={onToggleMobileNav}
 className="h-auto whitespace-normal [&_svg]:size-5 md:hidden p-2 hover:bg-accent rounded-lg transition-colors motion-reduce:transition-none"
 aria-label={mobileNavOpen ? t('a11y.closeSidebar') : t('a11y.openSidebar')}
 aria-expanded={mobileNavOpen ?? false}
 >
 <span
 className={`block transition-transform duration-200 motion-reduce:transition-none ${
 mobileNavOpen ? 'rotate-90' : 'rotate-0'
}`}
 >
 {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
 </span>
 </Button>
 )}
 {/* Workspace + nav-mode switchers are secondary chrome — below sm they
 fold into the kebab menu on the right so the breadcrumb keeps room. */}
 <div className="hidden sm:block">
 <WorkspaceSwitcher />
 </div>

 <span className="mx-1 h-4 w-px bg-border hidden sm:inline-block" />

 <div className="hidden sm:block">
 <NavModeSwitcher />
 </div>

 {/* Adaptive-UI transparency + reset control — only when the shell
 supplies the facade. Shows that the nav/dashboard adapted to real
 usage and lets the operator reset to the default order. */}
 {/* PRIORITY: this is the LOWEST-priority control in the left cluster —
 it explains an ordering the operator can also just look at. It used
 to appear from `md` (768px) and, at 1280px, that pushed the left
 cluster past its flex share so "Default order" painted 31px over the
 search field (measured; UX-A6). It now waits for `2xl`, where the
 measurement shows the row has room for it. */}
 {adaptive && (
 <>
 <span className="mx-1 h-4 w-px bg-border hidden 2xl:inline-block" />
 <div className="hidden 2xl:block shrink-0">
 <AdaptiveControl adaptive={adaptive} />
 </div>
 </>
 )}

 <span className="mx-1 h-4 w-px bg-border hidden sm:inline-block" />

 {/* Current-location label — static readout only. The navigable
 breadcrumb trail is PageHeader's (the design-system owner); the
 TopBar renders no second, competing trail. Shown on phones (where
 the switcher chrome is hidden and it's the only location cue in the
 bar) and on wide desktops; hidden in the crowded middle band where
 the fixed-width switcher chrome would squeeze it to an ellipsis. */}
 <span
 className="inline-block min-w-0 truncate text-sm font-medium text-foreground sm:hidden 2xl:inline-block"
 title={currentLocationLabel || 'Plant Canvas'}
 >
 {currentLocationLabel || 'Plant Canvas'}
 </span>
 </div>

 {/* Center: Cmd+K palette trigger */}
 <div className="flex-1 max-w-md hidden md:block">
 <Button
 type="button"
 variant="ghost"
 onClick={onOpenPalette}
 className="h-auto whitespace-normal justify-start text-left font-normal hover:text-muted-foreground flex w-full items-center gap-2 pl-3.5 pr-2 py-1.5 bg-surface-sunken/70 hover:bg-surface-sunken border border-transparent hover:border-border/60 rounded-full text-sm text-muted-foreground transition-colors"
 aria-label={t('a11y.openCommandPalette')}
 >
 <Search className="w-4 h-4 shrink-0" />
 <span className="flex-1 text-left">Search Tyndo…</span>
 <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-border bg-surface-raised px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground t-mono">
 ⌘K
 </kbd>
 </Button>
 </div>

 {/* Right: Actions */}
 <div className="flex items-center gap-1">
 {/* Mobile command-palette trigger — the full search box (center) is
 hidden below md, so without this there is no way to open the palette
 on a phone/tablet. Always visible below md; the ⌘K affordance stays a
 desktop-only enhancement. */}
 <Button
 type="button"
 variant="ghost"
 onClick={onOpenPalette}
 className="h-auto whitespace-normal [&_svg]:size-[18px] md:hidden p-2 hover:bg-accent rounded-lg transition-colors motion-reduce:transition-none"
 aria-label={t('a11y.openCommandPalette')}
 title={t('topbar.search')}
 >
 <Search className="h-[18px] w-[18px] text-muted-foreground" />
 </Button>

 {/* Theme Toggle — secondary; folds into the kebab below sm. */}
 <Button
 type="button"
 variant="ghost"
 onClick={toggleTheme}
 className="h-auto whitespace-normal [&_svg]:size-[18px] hidden sm:flex p-2 hover:bg-accent rounded-lg transition-colors"
 aria-label={darkMode ? t('a11y.switchToLightMode') : t('a11y.switchToDarkMode')}
 title={darkMode ? t('topbar.lightMode') : t('topbar.darkMode')}
 >
 {darkMode ? (
 <Sun className="h-[18px] w-[18px] text-alarm-minor" />
 ) : (
 <Moon className="h-[18px] w-[18px] text-muted-foreground" />
 )}
 </Button>

 {/* Notifications */}
 <Button
 type="button"
 variant="ghost"
 onClick={() => navigate('/tyndo.alarms')}
 className="h-auto whitespace-normal [&_svg]:size-[18px] relative p-2 hover:bg-accent rounded-lg transition-colors"
 aria-label={t('a11y.activeAlarms', { count: alarmCount })}
 title={t('topbar.activeAlarms')}
 >
 <Bell className="h-[18px] w-[18px] text-muted-foreground" />
 {/* aria-live so screen readers announce alarm-count changes without
 stealing focus from the operator's current task (polite). */}
 <span
 className="absolute top-1 right-1 min-w-[16px] h-[16px] flex items-center justify-center"
 aria-live="polite"
 aria-atomic="true"
 >
 {alarmCount > 0 && (
 <span className="bg-alarm-critical text-white text-[10px] font-bold rounded-full px-1 t-tabular min-w-[16px] h-[16px] flex items-center justify-center">
 {alarmCount > 99 ? '99+' : alarmCount}
 </span>
 )}
 </span>
 </Button>

 {/* The ONE Copilot trigger (docs/latest-analysis-ui-ux.md UX-A5).
 A live session found three on a single screen: this control, a
 floating action button, and an in-page suggestion card with its own
 Show/Refresh controls. The FAB is gone; this is now the only place
 an operator asks for the Copilot, at every width.

 Which means it can no longer be `lg:`-only. It used to hide below
 1024px and the FAB carried those widths, so deleting the FAB without
 widening this would have left 640–1023px with no Copilot control at
 all — and below 640px only the kebab. It shows from `sm` up, as an
 icon alone until there is room for the label and the shortcut chip,
 so it costs the crowded band (UX-A6) no horizontal space. */}
 <Button
 type="button"
 variant="ghost"
 onClick={openCopilot}
 className="h-auto whitespace-normal [&_svg]:size-3.5 font-normal hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 hover:bg-accent rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors"
 aria-label="AI Copilot"
 title="AI Copilot (⌘J)"
 >
 <Sparkles className="w-3.5 h-3.5" />
 <span className="hidden lg:inline">Copilot</span>
 <kbd className="hidden lg:inline rounded border border-border bg-surface-sunken px-1 py-0 text-[9px] t-mono">
 ⌘J
 </kbd>
 </Button>

 {/* Kebab — below sm, the secondary actions (workspace switcher, theme,
 density, settings) collapse in here so search/alarms/user keep their
 own touch targets. */}
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button
 type="button"
 variant="ghost"
 className="h-auto whitespace-normal [&_svg]:size-[18px] sm:hidden p-2 hover:bg-accent rounded-lg transition-colors motion-reduce:transition-none"
 aria-label={t('a11y.moreOptions')}
 title={t('topbar.moreOptions')}
 >
 <MoreVertical className="h-[18px] w-[18px] text-muted-foreground" />
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" className="w-60">
 {/* Below `sm` the visible Copilot trigger is hidden too, and the
 floating action button that used to cover these widths is gone
 (UX-A5). Without this item there would be no way to reach the
 Copilot on a phone at all — the ⌘J shortcut is not an answer
 where there is no keyboard. */}
 <DropdownMenuItem onSelect={openCopilot}>
 <Sparkles className="w-4 h-4 mr-2 text-muted-foreground" />
 Copilot
 </DropdownMenuItem>
 <DropdownMenuSeparator />
 <DropdownMenuLabel>{t('topbar.workspace')}</DropdownMenuLabel>
 <DropdownMenuSub>
 <DropdownMenuSubTrigger>
 <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
 <span className="truncate">{tenant?.name ?? 'Tyndo'}</span>
 </DropdownMenuSubTrigger>
 <DropdownMenuSubContent>
 {(tenants ?? []).map((tn) => (
 <DropdownMenuItem
 key={tn.tenantId}
 onSelect={() => setTenant(tn.tenantId)}
 >
 <span className="flex-1 truncate">{tn.name ?? tn.tenantId}</span>
 {tn.tenantId === tenant?.tenantId && (
 <Check className="w-3.5 h-3.5 text-primary" />
 )}
 </DropdownMenuItem>
 ))}
 </DropdownMenuSubContent>
 </DropdownMenuSub>
 <DropdownMenuSeparator />
 <DropdownMenuLabel>{t('topbar.appearance')}</DropdownMenuLabel>
 <DropdownMenuItem onSelect={() => toggleTheme()}>
 {darkMode ? (
 <Sun className="w-4 h-4 mr-2 text-muted-foreground" />
 ) : (
 <Moon className="w-4 h-4 mr-2 text-muted-foreground" />
 )}
 {darkMode ? t('topbar.lightMode') : t('topbar.darkMode')}
 </DropdownMenuItem>
 <DropdownMenuSub>
 <DropdownMenuSubTrigger>
 <Gauge className="w-4 h-4 mr-2 text-muted-foreground" />
 {t('topbar.density')}
 </DropdownMenuSubTrigger>
 <DropdownMenuSubContent>
 <DropdownMenuRadioGroup
 value={density}
 onValueChange={(value) => setDensity(value as DensityPreference)}
 >
 {DENSITY_OPTIONS.map((d) => (
 <DropdownMenuRadioItem key={d} value={d}>
 {densityLabel[d]}
 </DropdownMenuRadioItem>
 ))}
 </DropdownMenuRadioGroup>
 </DropdownMenuSubContent>
 </DropdownMenuSub>
 <DropdownMenuSeparator />
 <DropdownMenuItem onSelect={() => navigate('/settings')}>
 <Settings className="w-4 h-4 mr-2 text-muted-foreground" />
 {t('topbar.settings')}
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>

 <div className="w-px h-6 bg-border mx-1 hidden sm:block" />

 {/* User Menu */}
 <div className="relative" ref={userMenuRef}>
 <Button
 type="button"
 variant="ghost"
 onClick={() => setUserMenuOpen(!userMenuOpen)}
 className="h-auto whitespace-normal flex items-center gap-2 p-1 hover:bg-accent rounded-full transition-colors"
 aria-label={t('a11y.userMenu')}
 aria-expanded={userMenuOpen}
 >
 <div className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
 {user?.name?.charAt(0).toUpperCase() ||'U'}
 </div>
 </Button>

 {userMenuOpen && (
 <div className="glass absolute right-0 mt-2 w-64 rounded-xl shadow-[var(--shadow-lg)] z-50 py-1">
 <div className="px-3 py-2.5 border-b border-border">
 <p className="text-sm font-medium text-foreground truncate">
 {user?.name}
 </p>
 <p className="text-xs text-muted-foreground mt-0.5 truncate">
 {user?.email}
 </p>
 </div>
 {appearanceSection}
 <Button
 type="button"
 variant="ghost"
 onClick={() => {
 navigate('/profile');
 setUserMenuOpen(false);
}}
 className="h-auto whitespace-normal justify-start font-normal rounded-none w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors flex items-center gap-2.5"
 >
 <User className="w-4 h-4 text-muted-foreground" />
 Profile
 </Button>
 <Button
 type="button"
 variant="ghost"
 onClick={() => {
 navigate('/settings');
 setUserMenuOpen(false);
}}
 className="h-auto whitespace-normal justify-start font-normal rounded-none w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors flex items-center gap-2.5"
 >
 <Settings className="w-4 h-4 text-muted-foreground" />
 Settings
 </Button>
 {isAdmin && (
 <Button
 type="button"
 variant="ghost"
 onClick={() => {
 navigate('/widgets-admin');
 setUserMenuOpen(false);
}}
 className="h-auto whitespace-normal justify-start font-normal rounded-none w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors flex items-center gap-2.5"
 >
 <LayoutGrid className="w-4 h-4 text-muted-foreground" />
 Widget administration
 </Button>
 )}
 <div className="border-t border-border my-1" />
 <Button
 type="button"
 variant="ghost"
 onClick={handleLogout}
 className="h-auto whitespace-normal justify-start font-normal rounded-none w-full text-left px-3 py-2 text-sm text-alarm-critical hover:text-alarm-critical hover:bg-alarm-critical/10 transition-colors flex items-center gap-2.5"
 >
 <LogOut className="w-4 h-4" />
 Logout
 </Button>
 </div>
 )}
 </div>
 </div>
 </div>
 </header>
 );
}
